import { _ModuleSupport } from 'ag-charts-community';

const { motion } = _ModuleSupport;

type AnimatableRadialColumnDatum = {
    innerRadius: number;
    outerRadius: number;
    columnWidth: number;
    axisInnerRadius: number;
    axisOuterRadius: number;
    startAngle: number;
    endAngle: number;
};

type AngleKey = 'startAngle' | 'endAngle';
type AngleObject = Record<AngleKey, number>;
type FromToKey = 'from' | 'to';
type FromToObject = Record<FromToKey, number>;

export function createAngleMotionCalculator() {
    const angles: Record<AngleKey, Map<AngleObject, FromToObject>> = {
        startAngle: new Map(),
        endAngle: new Map(),
    };
    const angleKeys: AngleKey[] = ['startAngle', 'endAngle'];
    const calculate = (
        node: _ModuleSupport.Path & AngleObject,
        datum: AngleObject,
        status: _ModuleSupport.NodeUpdateState
    ) => {
        for (const key of angleKeys) {
            const map = angles[key];
            let from = (status === 'removed' || status === 'updated' ? node : datum)[key];
            let to = (status === 'removed' ? node : datum)[key];
            if (Number.isNaN(to)) {
                // eslint-disable-next-line sonarjs/deprecation
                to = node.unsafePreviousDatum?.[key] ?? Number.NaN;
            }
            const diff = from - to;
            if (Math.abs(diff) > Math.PI) {
                from -= Math.sign(diff) * 2 * Math.PI;
            }
            map.set(datum, { from, to });
        }
    };
    const getAngles = (datum: AngleObject, fromToKey: FromToKey) => {
        return {
            startAngle: angles.startAngle.get(datum)![fromToKey],
            endAngle: angles.endAngle.get(datum)![fromToKey],
        };
    };
    const from = (datum: AngleObject) => getAngles(datum, 'from');
    const to = (datum: AngleObject) => getAngles(datum, 'to');
    return { calculate, from, to };
}

export function fixRadialColumnAnimationStatus(
    node: _ModuleSupport.Path,
    datum: { startAngle: number; endAngle: number },
    status: _ModuleSupport.NodeUpdateState
) {
    if (status === 'updated') {
        if (
            node.previousDatum == null ||
            // eslint-disable-next-line sonarjs/deprecation
            Number.isNaN(node.unsafePreviousDatum.startAngle) ||
            // eslint-disable-next-line sonarjs/deprecation
            Number.isNaN(node.unsafePreviousDatum.endAngle)
        ) {
            return 'added';
        }
        if (Number.isNaN(datum.startAngle) || Number.isNaN(datum.endAngle)) {
            return 'removed';
        }
    }
    if (status === 'added' && node.previousDatum != null) {
        return 'updated';
    }
    return status;
}

export function prepareRadialColumnAnimationFunctions(axisZeroRadius: number) {
    const angles = createAngleMotionCalculator();

    const fromFn = (
        node: _ModuleSupport.RadialColumnShape,
        datum: AnimatableRadialColumnDatum,
        status: _ModuleSupport.NodeUpdateState
    ) => {
        status = fixRadialColumnAnimationStatus(node, datum, status);

        angles.calculate(node, datum, status);
        const { startAngle, endAngle } = angles.from(datum);

        let innerRadius: number;
        let outerRadius: number;
        let columnWidth: number;
        let axisInnerRadius: number;
        let axisOuterRadius: number;
        if (status === 'removed' || status === 'updated') {
            innerRadius = node.innerRadius;
            outerRadius = node.outerRadius;
            columnWidth = node.columnWidth;
            axisInnerRadius = node.axisInnerRadius;
            axisOuterRadius = node.axisOuterRadius;
        } else {
            innerRadius = axisZeroRadius;
            outerRadius = axisZeroRadius;
            columnWidth = datum.columnWidth;
            axisInnerRadius = datum.axisInnerRadius;
            axisOuterRadius = datum.axisOuterRadius;
        }

        const phase = motion.NODE_UPDATE_STATE_TO_PHASE_MAPPING[status];
        return {
            innerRadius,
            outerRadius,
            columnWidth,
            axisInnerRadius,
            axisOuterRadius,
            startAngle,
            endAngle,
            phase,
        };
    };

    const toFn = (
        node: _ModuleSupport.RadialColumnShape,
        datum: AnimatableRadialColumnDatum,
        status: _ModuleSupport.NodeUpdateState
    ) => {
        const { startAngle, endAngle } = angles.to(datum);

        let innerRadius: number;
        let outerRadius: number;
        let columnWidth: number;
        let axisInnerRadius: number;
        let axisOuterRadius: number;
        if (status === 'removed') {
            innerRadius = node.innerRadius;
            outerRadius = node.innerRadius;
            columnWidth = node.columnWidth;
            axisInnerRadius = node.axisInnerRadius;
            axisOuterRadius = node.axisOuterRadius;
        } else {
            innerRadius = Number.isNaN(datum.innerRadius) ? axisZeroRadius : datum.innerRadius;
            outerRadius = Number.isNaN(datum.outerRadius) ? axisZeroRadius : datum.outerRadius;
            columnWidth = Number.isNaN(datum.columnWidth) ? node.columnWidth : datum.columnWidth;
            axisInnerRadius = datum.axisInnerRadius;
            axisOuterRadius = datum.axisOuterRadius;
        }

        return { innerRadius, outerRadius, columnWidth, axisInnerRadius, axisOuterRadius, startAngle, endAngle };
    };

    return { toFn, fromFn };
}

export function resetRadialColumnSelectionFn(
    _node: _ModuleSupport.RadialColumnShape,
    {
        innerRadius,
        outerRadius,
        columnWidth,
        axisInnerRadius,
        axisOuterRadius,
        startAngle,
        endAngle,
    }: AnimatableRadialColumnDatum
) {
    return { innerRadius, outerRadius, columnWidth, axisInnerRadius, axisOuterRadius, startAngle, endAngle };
}
